/**
 * AddContactItemDialog - Dialog para selecionar/criar contato para campanha
 * 
 * Duas visualizações:
 * - Lista: busca e seleciona contatos existentes
 * - Criar: formulário para novo contato (acessível via botão "+")
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phone-input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  User, 
  Phone, 
  Briefcase, 
  Search, 
  Plus, 
  ArrowLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { MessageItem } from './types';
import { createLogger } from '@/lib/logger';

const logger = createLogger('AddContactItemDialog');

// =====================================================
// INTERFACES
// =====================================================

interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface AddContactItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    contactName: string;
    contactPhone: string;
    businessDescription?: string;
  }) => void;
  initialValue?: MessageItem | null;
  tenantId?: string;
}

// Schema de validação para criar novo contato
const contactSchema = z.object({
  contactName: z.string().min(1, 'Nome do contato é obrigatório'),
  contactPhone: z.string().min(10, 'Telefone deve ter pelo menos 10 dígitos'),
  businessDescription: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

// =====================================================
// FUNÇÕES AUXILIARES
// =====================================================

/**
 * Formata telefone para exibição
 */
function formatPhoneForDisplay(phone: string | null): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  
  // Formato brasileiro
  if (digits.startsWith('55') && digits.length >= 12) {
    const ddd = digits.slice(2, 4);
    const rest = digits.slice(4);
    if (rest.length === 9) {
      return `+55 (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
    } else if (rest.length === 8) {
      return `+55 (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
    }
  }
  
  return phone;
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function AddContactItemDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
  tenantId,
}: AddContactItemDialogProps) {
  // Estado de visualização: 'list' ou 'create'
  const [viewMode, setViewMode] = useState<'list' | 'create'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);

  // Formulário para criar novo contato
  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      contactName: '',
      contactPhone: '',
      businessDescription: '',
    },
  });

  // =====================================================
  // EFEITOS
  // =====================================================

  // Reset estado ao abrir/fechar
  useEffect(() => {
    if (open) {
      // Se estiver editando, vai direto para o formulário de criação
      if (initialValue?.item_type === 'contact') {
        setViewMode('create');
        form.reset({
          contactName: initialValue.contact_name || '',
          contactPhone: initialValue.contact_phone || '',
          businessDescription: initialValue.contact_business_description || '',
        });
      } else {
        setViewMode('list');
        setSearchTerm('');
        form.reset({
          contactName: '',
          contactPhone: '',
          businessDescription: '',
        });
      }
    }
  }, [open, initialValue, form]);

  // Busca contatos do tenant
  useEffect(() => {
    const fetchContacts = async () => {
      if (!open || !tenantId || viewMode !== 'list') return;
      
      setLoading(true);
      try {
        let query = supabase
          .from('contacts')
          .select('id, name, phone, email, avatar_url')
          .eq('tenant_id', tenantId)
          .order('name', { ascending: true, nullsFirst: false });
        
        if (searchTerm.trim()) {
          query = query.or(`name.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`);
        }
        
        const { data, error } = await query.limit(50);
        
        if (error) {
          logger.error('Erro ao buscar contatos', { error });
          return;
        }
        
        setContacts(data || []);
      } catch (error) {
        logger.error('Erro ao buscar contatos', { error });
      } finally {
        setLoading(false);
      }
    };

    // Debounce da busca
    const timeoutId = setTimeout(fetchContacts, 300);
    return () => clearTimeout(timeoutId);
  }, [open, tenantId, searchTerm, viewMode]);

  // =====================================================
  // HANDLERS
  // =====================================================

  /**
   * Seleciona contato existente
   */
  const handleSelectContact = (contact: Contact) => {
    logger.debug('Contato selecionado', { contact });
    onSave({
      contactName: contact.name || '',
      contactPhone: contact.phone?.replace(/\D/g, '') || '',
      businessDescription: '',
    });
    onOpenChange(false);
  };

  /**
   * Submete formulário de criação
   */
  const handleSubmit = (data: ContactFormData) => {
    logger.debug('Criando novo contato', { data });
    onSave({
      contactName: data.contactName,
      contactPhone: data.contactPhone.replace(/\D/g, ''),
      businessDescription: data.businessDescription || undefined,
    });
    onOpenChange(false);
  };

  /**
   * Volta para a lista
   */
  const handleBackToList = () => {
    setViewMode('list');
    form.reset({
      contactName: '',
      contactPhone: '',
      businessDescription: '',
    });
  };

  // =====================================================
  // RENDERIZAÇÃO
  // =====================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {viewMode === 'create' && !initialValue && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 -ml-2"
                onClick={handleBackToList}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <User className="h-5 w-5" />
            {initialValue 
              ? 'Editar Contato' 
              : viewMode === 'list' 
                ? 'Selecionar Contato' 
                : 'Novo Contato'
            }
          </DialogTitle>
        </DialogHeader>

        {/* Visualização: Lista de Contatos */}
        {viewMode === 'list' && (
          <div className="space-y-4">
            {/* Barra de busca + botão criar */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou telefone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setViewMode('create')}
                title="Criar novo contato"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Lista de contatos */}
            <ScrollArea className="h-[300px]">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
                  <User className="h-12 w-12 mb-2 opacity-50" />
                  <p className="text-center">
                    {searchTerm 
                      ? 'Nenhum contato encontrado' 
                      : 'Nenhum contato cadastrado'
                    }
                  </p>
                  <Button 
                    variant="link" 
                    onClick={() => setViewMode('create')}
                    className="mt-2"
                  >
                    Criar novo contato
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 pr-3">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => handleSelectContact(contact)}
                      className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer 
                                 hover:bg-accent transition-colors"
                    >
                      <Avatar className="h-10 w-10">
                        {contact.avatar_url && (
                          <AvatarImage src={contact.avatar_url} alt={contact.name || ''} />
                        )}
                        <AvatarFallback>
                          {contact.name?.charAt(0)?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {contact.name || 'Sem nome'}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {formatPhoneForDisplay(contact.phone)}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Visualização: Criar Novo Contato */}
        {viewMode === 'create' && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              {/* Nome do Contato */}
              <FormField
                control={form.control}
                name="contactName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Nome do Contato *
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ex: Empresa XYZ - Suporte"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Nome que aparecerá no cartão de contato
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Telefone */}
              <FormField
                control={form.control}
                name="contactPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Telefone *
                    </FormLabel>
                    <FormControl>
                      <PhoneInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="(99) 99999-9999"
                      />
                    </FormControl>
                    <FormDescription>
                      Número que será salvo na agenda do destinatário
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Descrição/Cargo */}
              <FormField
                control={form.control}
                name="businessDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Descrição (opcional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ex: Gerente Comercial, Suporte Técnico"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Informação adicional que aparecerá no cartão
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                {!initialValue && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBackToList}
                  >
                    Voltar
                  </Button>
                )}
                {initialValue && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancelar
                  </Button>
                )}
                <Button type="submit">
                  {initialValue ? 'Salvar' : 'Criar e Selecionar'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
